// The visitor's first paint (decision 136): the public pages are prerendered
// at build time as a visitor sees them, and the browser hydrates that markup
// instead of starting from a blank page. For the two renders to match, the
// browser primes the same answers the prerender had: no session, no client
// configuration yet. A pilot who signed in here before is remembered in the
// browser, so the page starts blank for them and never flashes the
// introduction before their fleet.

import type { QueryClient } from "@tanstack/react-query";

import type { ClientConfig } from "@/api";

const KEY = "saf.signed-in";

/** The answers a visitor's page starts from, stale at once so the real ones are fetched. */
export function primeVisitor(queryClient: QueryClient): void {
  queryClient.setQueryData(["me"], null, { updatedAt: 0 });
  queryClient.setQueryData<ClientConfig>(["config"], { maps: null, discord_invite: null }, { updatedAt: 0 });
}

/** Whether a sign-in was seen in this browser (a convenience, never the truth: the session decides). */
export function rememberedSignedIn(): boolean {
  try {
    return window.localStorage.getItem(KEY) === "1";
  } catch {
    return false;
  }
}

export function rememberSignedIn(signedIn: boolean): void {
  try {
    if (signedIn) window.localStorage.setItem(KEY, "1");
    else window.localStorage.removeItem(KEY);
  } catch {
    // Storage away (a private window, blocked site data): the next load starts blank, as before.
  }
}

/**
 * Where the shell's window starts: it opens on `app.html` (the clean shell,
 * decision 136), a path no page answers to, so under the host the address is
 * set to `/` before the router looks at it (0.2.0-82 opened on "Page not
 * found", 2026-11-03). In a browser the path is the page asked for and stays.
 */
export function entryPath(pathname: string, underHost: boolean): string | null {
  return underHost && /\.html$/i.test(pathname) ? "/" : null;
}

/**
 * Whether the browser should hydrate the markup it found: the page was
 * prerendered for this very path, for a visitor, and no sign-in is
 * remembered here. Under the companion's shell the pages are drawn afresh.
 */
export function shouldHydrate(root: HTMLElement, pathname: string, underHost: boolean): boolean {
  const prerendered = root.dataset.prerendered;
  if (underHost || prerendered === undefined) return false;
  const path = pathname.replace(/\/+$/, "") || "/";
  return prerendered === path && root.hasChildNodes() && !rememberedSignedIn();
}
